import { useState, useEffect } from "react";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import useCurrentUser from "hooks/useCurrentUser";
import {
  PageContainer, StatCard, PremiumCard, DataTable, StatusBadge,
  LoadingState, EmptyState,
} from "components/ui";

const IconTag = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <line x1="7" y1="7" x2="7.01" y2="7" />
  </svg>
);
const IconCoins = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
  </svg>
);
const IconActivity = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

const BranchAdminDashboard = () => {
  const axiosPrivate = useAxiosPrivate();
  const { user, loading: userLoading } = useCurrentUser();
  const branch = user?.branch;

  const [stockStats, setStockStats] = useState({ totalProducts: 0, lowStock: 0, totalValue: 0 });
  const [recentMovements, setRecentMovements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!branch) return;
    const fetchData = async () => {
      try {
        // const statsRes = await axiosPrivate.get(`/api/branches/${branch.id}/stock-stats`);
        // setStockStats(statsRes.data);
        const mRes = await axiosPrivate.get(`/api/branches/${branch.id}/movements?page=0&size=5`);
        setRecentMovements(mRes.data.content);
      } catch (err) {
        console.error("Failed to fetch branch data", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [branch]);

  if (userLoading || loading) {
    return (
      <PageContainer>
        <PremiumCard><LoadingState label="Loading branch overview…" /></PremiumCard>
      </PageContainer>
    );
  }

  if (!branch) {
    return (
      <PageContainer>
        <PremiumCard>
          <EmptyState title="No branch assigned" description="Ask your tenant admin to assign you to a branch." />
        </PremiumCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Branch identity */}
      <PremiumCard
        className="mt-4"
        title={branch.name}
        subtitle={[branch.address, branch.city].filter(Boolean).join(" · ") || "No address"}
        actions={<StatusBadge value={branch.status || "ACTIVE"} dot />}
      />

      {/* KPI strip */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginBottom: 24,
          marginTop:24,
        }}
      >
        <StatCard
          label="Total products"
          value={stockStats.totalProducts}
          icon={<IconTag />}
          hint={`${stockStats.lowStock} low-stock`}
        />
        <StatCard
          label="Inventory value"
          value={`${stockStats.totalValue} TND`}
          icon={<IconCoins />}
        />
        <StatCard
          label="Recent movements"
          value={recentMovements.length}
          icon={<IconActivity />}
        />
      </div>

      {/* Recent movements */}
      <DataTable
        title="Recent stock movements"
        subtitle="Last 5 changes on this branch"
        data={recentMovements}
        rowKey={(m) => m.id}
        search={false}
        paginate={false}
        emptyTitle="No movements yet"
        emptyDescription="As soon as inventory changes happen, they'll appear here."
        columns={[
          { key: "product", header: "Product", render: (m) => m.product?.name || "—" },
          {
            key: "qty", header: "Change", align: "right",
            render: (m) => (
              <span className={m.quantityChange > 0 ? "t-success" : "t-danger"} style={{ fontWeight: 600, fontFamily: "var(--font-mono)" }}>
                {m.quantityChange > 0 ? "+" : ""}{m.quantityChange}
              </span>
            ),
          },
          { key: "reason", header: "Type", render: (m) => <StatusBadge value={m.reason} /> },
          { key: "createdAt", header: "Date", render: (m) => <span className="t-muted">{new Date(m.createdAt).toLocaleString()}</span> },
        ]}
      />
    </PageContainer>
  );
};

export default BranchAdminDashboard;
